import type { Campaign } from '~/modules/campaign/types';
import type { CampaignPriority } from '~/types/graphql';
import type { IAnalyticMetric } from '~/types/metric';

import { CAMPAIGN_STATUS, CAMPAIGN_PRIORITY, CAMPAIGN_TYPE } from '~/constants/campaign';
import { useCampaignAnalyticMetricDetect } from './useCampaignAnalyticMetricDetect';
import { useConversionRateWinner } from './useConversionRateWinner';
import { hasWinningVariant } from '~/modules/make-winner/utils';
import { IVariantType } from '~/modules/campaign/types';
import { useFeatureFlagChecker } from '~/modules/apps';
import { determineMetricWinner } from '../helpers';
import { useMemo } from 'react';

interface UseShowMakeWinnerButtonParams {
  campaign?: Campaign;
  metrics: IAnalyticMetric[];
  goal?: CampaignPriority;
}

export const useShowMakeWinnerButton = ({
  campaign,
  metrics,
  goal,
}: UseShowMakeWinnerButtonParams) => {
  const { isEnabledMakeWinnerButton } = useFeatureFlagChecker();

  const hasWinner = hasWinningVariant(campaign?.variants);
  const winningByCR = useConversionRateWinner({ metrics });

  const metricA = useCampaignAnalyticMetricDetect({
    metric: metrics.find((m) => m.variantKey === IVariantType.VARIANT_A),
  });
  const metricB = useCampaignAnalyticMetricDetect({
    metric: metrics.find((m) => m.variantKey === IVariantType.VARIANT_B),
  });

  const isWinnerDetermined = useMemo(() => {
    if (goal === CAMPAIGN_PRIORITY.CONVERSION) {
      const isWinningDataInvalid = winningByCR?.every((m) => m.confidence === 0);
      if (isWinningDataInvalid) return false;

      return true;
    }
    if (goal === CAMPAIGN_PRIORITY.REVENUE) {
      const { isVariantALarger, isVariantBLarger } = determineMetricWinner(
        metricA,
        metricB,
        'totalRevenueMetric',
      );
      return isVariantALarger || isVariantBLarger;
    }
    return false;
  }, [goal, winningByCR, metricA, metricB]);

  // Price and URL tests have no make-winner flow yet.
  const isMakeWinnerUnsupported =
    campaign?.type === CAMPAIGN_TYPE.PRICE || campaign?.type === CAMPAIGN_TYPE.URL;

  const isShowMakeWinnerButton =
    !isMakeWinnerUnsupported &&
    campaign &&
    !hasWinner &&
    campaign?.status !== CAMPAIGN_STATUS.STOPPED &&
    isWinnerDetermined;

  return {
    isShowMakeWinnerButton:
      !isMakeWinnerUnsupported && (isEnabledMakeWinnerButton || isShowMakeWinnerButton),
    hasWinner,
  };
};
